/**
 * useCauseDetail(causeKey, learnedKey): the cause write-up of GET /api/v5/brain/cause_detail?session_id=&cause_key=PC_<n>
 * (brain_api.schema.json cause_detail), the same data the old diagnosis map's cause detail showed. Cached per
 * (cause, learnedKey) in module scope; a 'pending' write-up (the server is still writing the summary) is asked again every
 * WRITEUP_POLL_MS up to WRITEUP_MAX_POLLS, like the vendored rhs4_causedetail.js.
 *
 * Returns {status: 'idle'|'loading'|'ok'|'fail', data: cause_detail | null}.
 */
import { useEffect, useState } from "react";

import { BRAIN_ENDPOINTS, brainGet } from "../../../vendor/brain";

export const DETAIL_STATUS = Object.freeze({ IDLE: "idle", LOADING: "loading", OK: "ok", FAIL: "fail" });
const WRITEUP_PENDING = "pending";
const WRITEUP_POLL_MS = 1500;
const WRITEUP_MAX_POLLS = 8;
const ERROR_KIND = "error";
const CACHE_MAX = 64;

const cache = new Map();   // `${causeKey}|${learnedKey}` -> {status, data, polls, listeners: Set<fn>}
const IDLE = Object.freeze({ status: DETAIL_STATUS.IDLE, data: null });

const notify = (entry) => entry.listeners.forEach((fn) => fn({ status: entry.status, data: entry.data }));
const isPending = (data) => data?.writeup?.status === WRITEUP_PENDING;

function fetchInto(entry, causeKey, key) {
  brainGet(BRAIN_ENDPOINTS.CAUSE_DETAIL, { cause_key: causeKey })
    .then((r) => (r.ok && r.data && r.data.kind !== ERROR_KIND ? r.data : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((data) => {
      entry.status = DETAIL_STATUS.OK;
      entry.data = data;
      notify(entry);
      if (!isPending(data) || entry.polls >= WRITEUP_MAX_POLLS) return;
      entry.polls += 1;
      setTimeout(() => { if (cache.get(key) === entry) fetchInto(entry, causeKey, key); }, WRITEUP_POLL_MS);
    })
    .catch(() => {
      if (entry.status === DETAIL_STATUS.LOADING) { entry.status = DETAIL_STATUS.FAIL; notify(entry); }
    });
}

function entryFor(causeKey, learnedKey) {
  const key = `${causeKey}|${learnedKey}`;
  if (cache.has(key)) return cache.get(key);
  const entry = { status: DETAIL_STATUS.LOADING, data: null, polls: 0, listeners: new Set() };
  cache.set(key, entry);
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
  fetchInto(entry, causeKey, key);
  return entry;
}

export function resetCauseDetailCache() {
  cache.clear();
}

export default function useCauseDetail(causeKey, learnedKey = "") {
  const [state, setState] = useState(IDLE);
  useEffect(() => {
    if (!causeKey) { setState(IDLE); return undefined; }
    const entry = entryFor(causeKey, learnedKey);
    setState({ status: entry.status, data: entry.data });
    entry.listeners.add(setState);
    return () => { entry.listeners.delete(setState); };
  }, [causeKey, learnedKey]);
  return state;
}
